/* ============================================================
   Cost model + small formatters for the Auto-Dub picker flow.
   PRD §7.2 — estimated card cost = duration_seconds × CREDIT_RATE
   per language; aggregate = sum over matched videos × languages.
   ============================================================ */
import { CREDIT_RATE } from './mockData.js';

/* billable duration — rounded UP to the next whole minute, so any partial
   minute counts as a full one (57s → 60s, 1:05 → 120s). */
export const billableSeconds = (sec) => Math.ceil((sec || 0) / 60) * 60;

/* per-video credit cost for one language (charged on billable minutes) */
export const videoCost = (v) => Math.round(billableSeconds(v.dur) * CREDIT_RATE);

/* mm:ss duration label */
export function fmtDuration(sec) {
	const m = Math.floor(sec / 60);
	const s = sec % 60;
	return `${m}:${String(s).padStart(2, '0')}`;
}

/* compact view count: 1.4M / 920K / 540 */
export function fmtViews(n) {
	if (n >= 1000000) return (n / 1000000).toFixed(n % 1000000 ? 1 : 0).replace(/\.0$/, '') + 'M';
	if (n >= 1000) return Math.round(n / 1000) + 'K';
	return String(n);
}

/* compact number used on credit bars: 142k / 250k / 1.3M */
export function fmtK(n) {
	if (n >= 1000000) return (n / 1000000).toFixed(n % 1000000 ? 1 : 0).replace(/\.0$/, '') + 'M';
	if (n >= 1000) return Math.round(n / 1000) + 'k';
	return String(n);
}

/* whether any filter rule is active (drives "Clear all" + summary copy) */
export function hasAnyFilter(f) {
	return !!(
		f.titleKeywords?.length || f.tags?.length || f.playlists?.length ||
		f.dateFrom || f.dateTo || f.minViews || f.durMin || f.durMax ||
		(f.shorts && f.shorts !== 'include')
	);
}

/* AND-combine all filter rules against the video library (PRD §7.1) */
export function matchVideos(videos, f) {
	return videos.filter(v => {
		if (f.titleKeywords?.length) {
			const t = v.title.toLowerCase();
			const hits = f.titleKeywords.map(k => t.includes(k.toLowerCase()));
			if (f.titleMode === 'all' ? !hits.every(Boolean) : !hits.some(Boolean)) return false;
		}
		if (f.tags?.length && !f.tags.some(tg => v.tags.includes(tg.toLowerCase()))) return false;
		if (f.playlists?.length && !(v.playlists ?? [v.playlist]).some(p => f.playlists.includes(p))) return false;
		if (f.dateFrom && v.date < f.dateFrom) return false;
		if (f.dateTo && v.date > f.dateTo) return false;
		if (f.minViews && v.views < +f.minViews) return false;
		if (f.durMin && v.dur < +f.durMin * 60) return false;
		if (f.durMax && v.dur > +f.durMax * 60) return false;
		if (f.shorts === 'exclude' && v.type === 'short') return false;
		if (f.shorts === 'only' && v.type !== 'short') return false;
		return true;
	});
}

/* aggregate cost across matched videos for a given language count.
   `billableMin` mirrors the credit model: each video's duration is rounded UP to
   a whole minute (15s → 1, 1:12 → 2) before summing — not a single round of the
   raw total. `totalSec` is the raw second sum (kept for any exact-time needs). */
export function aggregateCost(matched, langCount = 1) {
	const perLang = matched.reduce((a, v) => a + videoCost(v), 0);
	const total = perLang * Math.max(1, langCount);
	const totalSec = matched.reduce((a, v) => a + (v.dur || 0), 0);
	const billableMin = matched.reduce((a, v) => a + billableSeconds(v.dur) / 60, 0);
	return { perLang, total, totalSec, billableMin };
}
